import { CdkTreeNode, FlatTreeControl } from '@angular/cdk/tree';
import { ElementRef, EventEmitter, Injectable, OnDestroy, OnInit, Output, QueryList } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { AppHelper } from '@app/core/app.helper';
import { InputSearchComponent } from '@components/input-search/component/input-search.component';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { INode } from '../interfaces/INode';
import { InterfaceTreeClass } from '../interfaces/InterfaceTreeClass';
import { TreeServiceClass } from './treeService.class';
/**
 * Класс компонентов дерева в приложении
 * @package Tree
 * @version 1.0
 * @author BOLOTOV I.P.
 * @since 31.08.2022
 * @copyright (c) Internet-Frigate {@link http://www.ifrigate.ru}
 */
@Injectable()
/** Класс компонентов дерева в приложении */
export abstract class TreeComponentClass implements InterfaceTreeClass, OnInit, OnDestroy {
    public nodesList!: QueryList<CdkTreeNode<any>>;
    public nodesRefList!: QueryList<ElementRef>;
    public treeContainer!: ElementRef;

    public searchRowRef!: InputSearchComponent;

    /** Массив с одним узлом для ввода в нового значения */
    public inputNode$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);

    /** Узнать, создается ли сейчас новый узел */
    public isCreatingNewNode$: Observable<boolean> = this.inputNode$.pipe(map((nodes) => nodes.length > 0));

    public searchType = {
        search: {
            icon: 'filter-outline',
            positionIcon: 'right',
            showToggleArrowButtons: true,
        },
        searchClick: {
            icon: 'filter-outline',
            positionIcon: 'right',
            showToggleArrowButtons: true,
        },
        filter: {
            icon: 'filter-outline',
            positionIcon: 'left',
            showToggleArrowButtons: false,
        },
    };
    /** Набор от плоского узла к вложенному узлу. Это помогает находить изменяемый вложенный узел. */
    flatNodeMap: Map<number | string, INode> = new Map<number | string, INode>();

    /**
     * Набор от вложенного узла до плоского узла.
     * Это помогает сохранить один и тот же объект для выбора.
     */
    nestedNodeMap: Map<INode, INode> = new Map<INode, INode>();
    /**  Флаг - раскрыто ли все дерево */
    expandAll: boolean = false;
    /** Control для дерева */
    treeControl: FlatTreeControl<INode>;

    /** Control для дерева с полем добавления нового узла */
    defaultTreeControl: FlatTreeControl<INode>;

    /**
     * Сглаживатель дерева для преобразования нормального типа узла в узел с информацией
     * о дочерних элементах и уровне
     */
    treeFlattener: MatTreeFlattener<INode, INode>;
    /** Строка для поиска по дереву */
    treeSearch: string = '';
    /** Событие выбора узла дерева */
    selectItem = new EventEmitter<INode | INode[]>();
    /** Слушатель изменений поля фильтра */
    filterChange$: BehaviorSubject<string> = new BehaviorSubject('');
    /** Событие добавление нового элемента */
    @Output() eventAdd: EventEmitter<INode> = new EventEmitter<INode>();

    @Output() validateTree: EventEmitter<{ isError: boolean }> = new EventEmitter<{ isError: boolean }>();
    /** Стрелочная функция, для получения уровня на котором находится узел */
    protected _getLevel = (node: INode) => node.level;
    /** Стрелочная функция, для получения уровня на котором находится узел */
    protected _isExpandable = (node: INode) => node.expandable;
    /** Стрелочная функция, для получения вложенных узлов */
    protected _getChildren = (node: INode): Observable<INode[]> => of(node.children);

    /**
     * Обработка записей дерева
     * @param node ветка дерева
     * @param level уровень в дереве
     * @private
     */
    protected _transformer = (node: INode, level: number): INode => {
        const existingNode = this.nestedNodeMap.get(node);
        const flatNode = existingNode && existingNode === node ? existingNode : node;
        flatNode.expandable = !!node.children && node.children.length > 0;
        flatNode.level = level;
        this.flatNodeMap.set(flatNode.id, flatNode);
        this.nestedNodeMap.set(node, flatNode);
        if (this.service.config.useDefaultIcons) {
            node.icon = this.getDefaultNodeIcon(node);
        }
        return flatNode;
    };

    /**
     * Конструктор класса
     * @param service
     * @protected
     */
    protected constructor(public service: TreeServiceClass) {
        // Установки для управления деревом material
        this.treeFlattener = new MatTreeFlattener(
            this._transformer,
            this._getLevel,
            this._isExpandable,
            this._getChildren,
        );
        this.treeControl = new FlatTreeControl<INode>(this._getLevel, this._isExpandable);
        this.defaultTreeControl = new FlatTreeControl<INode>(this._getLevel, this._isExpandable);
        this.service.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    }

    /** Уничтожение компонента */
    ngOnDestroy(): void {
        this.inputNode$.complete();
        this.eventAdd?.complete();
        this.validateTree.complete();
    }

    ngOnInit(): void {
        this.service.treeData = [];

        // Установка вида поисковой строки
        if (!this.service.config.hasOwnProperty('filterType')) {
            this.service.config.filterType = 'search';
        }

        // Формируем вложенный список из плоского списка по parent_id
        if (this.service?.config?.listNodes) {
            this.service.prepareTreeData();
            // Раскрываем узлы дерева до определенного уровня
            if (this.service?.config?.levelOpen > 0) {
                this.expandUpToLevel(
                    this.service.dataSource.data,
                    this.service?.config?.levelOpen ? parseInt(this.service?.config?.levelOpen + '', 10) - 1 : 0,
                );
            }
        }
        // Установка подписки поиска по дереву
        this.filterChange$
            .pipe(
                debounceTime(1000),
                filter(() => this.searchType[this.service?.config?.filterType]?.showToggleArrowButtons),
                distinctUntilChanged(),
            )
            .subscribe((value) => {
                this.treeSearch = value;
                if (this.service.config.filterType === 'filter' || this.service.config.filterType === 'search') {
                    this.filterService(value);
                }
            });
        this.cancelAddNewNode();
    }

    /** Обновление дерева */
    public setOptions() {
        this.cancelAddNewNode();

        const openNodeID = [];
        this.treeControl.expansionModel.selected.forEach((item: INode) => {
            openNodeID.push(item?.id);
        });

        this.service.prepareTreeData();

        if (openNodeID.length) {
            openNodeID.forEach((id) => {
                const nodeData = this.flatNodeMap.get(id);
                if (nodeData) {
                    this.treeControl.expand(nodeData);
                }
            });
        } else if (this.service?.config?.levelOpen > 0) {
            this.expandUpToLevel(
                this.service.dataSource.data,
                this.service?.config?.levelOpen ? parseInt(this.service?.config?.levelOpen + '', 10) - 1 : 0,
            );
        }
    }

    /**
     * Раскрытие дерева до определенного уровня
     * @param nodes узел
     * @param level уровень до которого необходимо раскрывать
     */
    private expandUpToLevel(nodes: INode[], level: number): void {
        nodes.forEach((node) => {
            if (level >= node.level) {
                this.treeControl.expand(node);
                if (node.children && node.children.length > 0) {
                    this.expandUpToLevel(node.children, level);
                }
            }
        });
    }

    /** Свернуть и развернуть дерево */
    expandAllToggle(): void {
        if (this.expandAll) {
            this.treeControl.collapseAll();
            this.expandAll = false;
        } else {
            this.treeControl.expandAll();
            this.expandAll = true;
        }
    }
    /**
     * Изменение состояния фильтра
     * @param search искомая строка
     */
    changeFilterRow(search: string): void {
        search = String(search ?? '');
        this.treeSearch = search;
        this.clearSearchData();
        // Экранируем строку
        const searchStr = AppHelper.escapingStringForSearch(search);
        const serchExp = new RegExp(searchStr, 'i');
        // Убираем изменение структуры дерева и получаем количество найденных элементов
        if (searchStr.length > 0) {
            this.service.filterResult = this.service.searchTreeDataFlat(this.service.dataSource.data, serchExp, []);
            // Раскрываем найденные элементы.
            this.service.filterResult.forEach((item) => {
                let elem = item;
                while (elem?.level && elem?.level > 0) {
                    this.treeControl.expand(elem);
                    elem = this.flatNodeMap.get(elem?.parent_id);
                }
                this.treeControl.expand(elem);
            });
            if (this.service.filterResult.length) {
                setTimeout(() => this.showFilteredNode(1));
            }
        }
    }

    /**
     * Фильтр для поиска по дереву
     * @param search искомая строка
     */
    public filterService(search: string): void {
        search = String(search ?? '');
        this.treeSearch = search;
        this.clearSearchData();
        // Экранируем строку
        const searchStr = AppHelper.escapingStringForSearch(search);
        const serchExp = new RegExp(searchStr, 'i');

        // Проверка и поиск совпадений по имени
        if (searchStr.length > 0) {
            this.service.dataSource.data = this.service.searchTreeData(this.service.treeData, serchExp);
            this.treeControl.expandAll();
            this.expandAll = true;
        } else {
            this.service.dataSource.data = this.service.treeData;
        }
    }

    /**
     * Сбрасывает данные поиска
     */
    private clearSearchData(): void {
        this.service.filterResult = [];
        this.service.filterResultIndex = -1;
        this.service.focusedNode = null;
        this.service.selectNode = null;
    }

    /**
     * Click по узлу дерева
     * @param node - узел дерева
     */
    clickNode(node: INode): void {
        // Убираем фокусировку
        this.service.focusedNode = null;
        // Обработка флага на выбор папки
        if (
            node?.is_folder &&
            !this.service.config.isFolderSelect &&
            this.service.config.isFolderSelect !== undefined
        ) {
            return;
        }
        // Обработка - кликнули на уже выбранную ветвь
        if (this.service.selectNode === node) {
            this.service.selectNode = null;
            return;
        }
        // Создаем событие клика на ветку и передаем в родительский компонент
        if (this.service.config.isMultiple) {
            if (node.children.length > 0) {
                this.itemSelectionToggle(node);
            } else {
                this.leafItemSelectionToggle(node);
            }
        } else {
            // Устанавливаем ветвь выбранной
            this.service.selectNode = node;
            this.selectItem.next(node);
        }
    }

    /**
     * Определение уровня вложенности узда
     * @param _ номер итерации
     * @param node ветка дерева
     */
    hasChild = (_: number, node: INode) => node?.children.length > 0 || (node.is_folder && node.is_folder === true);

    isCreateNewNode = (_: number, node: INode) => node.id === 'createNewNode';

    /**
     * Проверка верхнего элемента дерева
     * @param node ветка дерева
     */
    firstChild = (node: INode) => node?.level === 0;

    /**
     * Проверка всех родителей, чтобы увидеть, если они изменились
     * @param node узел
     */
    leafItemSelectionToggle(node: INode): void {
        this.service.checklistSelection.toggle(String(node?.id));
        this.checkAllParentsSelection(node);
    }

    /**
     * Проверяет всех родителей, когда конечный узел выбран / не выбран
     * @param node узел
     */
    checkAllParentsSelection(node: INode): void {
        let parent: INode | null = this.flatNodeMap.get(node.parent_id);
        while (parent) {
            this.checkRootNodeSelection(parent);
            parent = this.flatNodeMap.get(parent.parent_id);
        }
    }
    /**
     * Проверка состояние корневого узла и изменение его соответствующим образом
     * @param node узел
     */
    checkRootNodeSelection(node: INode): void {
        const nodeSelected = this.service.checklistSelection.isSelected(String(node?.id));
        const descendants = this.treeControl.getDescendants(node);
        const descAllSelected = descendants.every((child) =>
            this.service.checklistSelection.isSelected(String(child?.id)),
        );
        if (nodeSelected && !descAllSelected) {
            this.service.checklistSelection.deselect(String(node?.id));
        } else if (!nodeSelected && descAllSelected) {
            this.service.checklistSelection.select(String(node?.id));
        }
    }

    /**
     * Выбрать/отменить выбор всех потомков
     * @param node ветка
     */
    itemSelectionToggle(node: INode): void {
        this.service.checklistSelection.toggle(String(node?.id));
        const descendants = this.treeControl.getDescendants(node).map((item) => String(item.id));
        let isCheck = this.service.checklistSelection.isSelected(String(node?.id));

        if (this.descendantsPartiallySelected(node)) {
            isCheck = true;
        }
        if (isCheck) {
            this.service.checklistSelection.select(...descendants);
        } else {
            this.service.checklistSelection.deselect(...descendants);
        }
        // // Принудительное обновление для родителя
        descendants.every((child) => this.service.checklistSelection.isSelected(child));
        this.checkAllParentsSelection(node);
        if (this.service.config?.isMultiple) {
            this.selectItem.next(this.service.checklistSelection.selected.map((id) => this.flatNodeMap.get(id)));
        }
    }

    /**
     * Является ли часть потомков выбранными
     * @param node ветка
     */
    descendantsPartiallySelected(node: INode): boolean {
        const descendants = this.treeControl.getDescendants(node);
        const result = descendants.some((child) => this.service.checklistSelection.isSelected(String(child?.id)));
        return result && !this.descendantsAllSelected(node);
    }

    /**
     * Все ли потомки узла выбраны
     * @param node ветка
     */
    descendantsAllSelected(node: INode): boolean {
        const descendants = this.treeControl.getDescendants(node);

        if (descendants.length) {
            return descendants.every((child) => this.service.checklistSelection.isSelected(String(child?.id)));
        }

        return this.service.checklistSelection.isSelected(String(node?.id));
    }

    /**
     * Прокрутка поиска по дереву
     * @param count - ступень прокрутки
     */
    showFilteredNode(count: -1 | 1): void {
        this.service.filterResultIndex += count;

        const foundNodes: any[] = [];
        this.flatNodeMap.forEach((flatNode) => {
            const resultNode = this.service.filterResult.find((x) => x.id === flatNode.id);
            if (resultNode) {
                foundNodes.push(resultNode);
            }
        });

        const nodeId = foundNodes[this.service.filterResultIndex].id;
        if (!nodeId) {
            return;
        }

        const node = this.nodesList.find((x) => x.data.id === nodeId);
        const nodeRef = this.nodesRefList.find((x) => `${x.nativeElement.id}` === `${nodeId}`);
        if (node && nodeRef) {
            this.service.focusedNode = node.data;
            if (this.service.config.isMultiple) {
                nodeRef.nativeElement?.querySelector('label').focus();
            } else {
                nodeRef.nativeElement?.focus();
            }
        }
    }

    /** Создаем дерево для добавления нового узла */
    public openTreeForCreateNode(): void {
        const strSearch = this.filterChange$.value;
        this.searchRowRef.clearForm();
        this.cancelAddNewNode();
        this.validateTree.next({ isError: true });
        this.inputNode$.next([
            {
                id: 'createNewNode',
                is_folder: false,
                name: strSearch,
                level: 1,
                parent_id: this.service.dataSource.data[0]?.parent_id,
            },
        ]);
    }

    /**
     * Окончание перемещения узла для создания элемента
     * @param event - данные события окончания перемещения
     */
    public dropInputNode(event: any): void {
        const i = event.currentIndex > event.previousIndex ? 1 : -1;

        const affectedIndex = this.inputNode$.value.length > 0 ? -1 : 0;

        const affectedNode = event.container.getSortedItems()[event.currentIndex + affectedIndex]?.data;

        const node = event.item.data as INode;

        // 1. Удалить узел из старого места
        if (node.parent_id === 0) {
            this.inputNode$.next([]);
        } else {
            let parentDelNode: INode;
            if (this.flatNodeMap.has(String(node.parent_id))) {
                parentDelNode = this.flatNodeMap.get(String(node.parent_id));
            } else {
                parentDelNode = { children: this.service.dataSource.data };
            }
            const delIndex = parentDelNode.children.findIndex((item) => item.id === node.id);
            parentDelNode.children.splice(delIndex, 1);
        }

        // 2. Определяем параметры для вставки

        let newParentNode: INode = null;
        let insertIndex = 0;

        if (affectedNode.is_folder) {
            const isOpenAffectedNode = this.treeControl.isExpanded(this.flatNodeMap.get(affectedNode.id));

            if (isOpenAffectedNode && i === 1) {
                newParentNode = this.flatNodeMap.get(affectedNode.id);
            } else if (!isOpenAffectedNode && this.flatNodeMap.has(affectedNode.parent_id)) {
                newParentNode = this.flatNodeMap.get(affectedNode.parent_id);
            } else {
                insertIndex = this.service.dataSource.data.findIndex((item) => item.id == affectedNode.id) + 1;
                newParentNode = {
                    children: this.service.dataSource.data,
                    level: -1,
                    id: this.service.dataSource.data[0].parent_id,
                };
            }
        } else {
            if (this.flatNodeMap.has(affectedNode.parent_id)) {
                newParentNode = this.flatNodeMap.get(affectedNode.parent_id);
                insertIndex = newParentNode.children.findIndex((item) => item.id == affectedNode.id) + 1;
            } else {
                insertIndex = this.service.dataSource.data.findIndex((item) => item.id == affectedNode.id) + 1;
                newParentNode = {
                    children: this.service.dataSource.data,
                    level: -1,
                    id: this.service.dataSource.data[0].parent_id,
                };
            }
        }

        // 3. Обновляем данные узла

        node.parent_id = newParentNode.id;
        node.level = newParentNode.level + 1;

        // 4. Вставляем узел в новое место

        if (i === -1) {
            insertIndex = insertIndex - 1;
        }
        newParentNode.children.splice(insertIndex, 0, node);

        // 5. Обновляем данные

        const data = this.service.dataSource.data;
        this.service.dataSource.data = data;
    }

    /** Отмена добавление и удаление узла */
    public cancelAddNewNode(): void {
        this.validateTree.next({ isError: false });
        // Если есть старый узел для создания, сначала удаляем его
        if (this.flatNodeMap.has('createNewNode')) {
            const node = this.flatNodeMap.get('createNewNode');
            const parent = this.flatNodeMap.get(node.parent_id);
            if (parent) {
                const index = parent.children.findIndex((item) => item.id === node.id);
                if (index > -1) {
                    parent.children.splice(index, 1);
                }
            }
            const data = this.service.dataSource.data;
            this.service.dataSource.data = data;
            this.service.treeData = this.service.dataSource.data;
        }
        this.inputNode$.next([]);
        this.service.dataSource.data = [];
        this.service.dataSource.data = this.service.treeData;
    }

    /**
     * Добавление нового узла
     * @param node - данные узла
     */
    public saveNewNode(node: INode): void {
        this.eventAdd.next({
            parent_id: node.parent_id,
            name: node.name,
        });
    }

    /**
     * Установка выбранных узлов
     * @param ids - список id узлов
     */
    public setSelectedItems(ids: string[] | number[]): void {
        this.service.config.valueData = ids;
        ids.forEach((id) => {
            const node = this.flatNodeMap.get(id);
            if (node) {
                this.service.checklistSelection.select(String(node?.id));
            }
        });
    }

    /**
     * Переключить состояние узла
     * @param node - узел
     */
    public toggleNode(node): void {
        this.treeControl.toggle(node);
    }

    /**
     * Поставляем иконку папке в зависимости от того, открыта она или нет
     * @param node
     */
    public getNodeIcon(node): string {
        if (node.icon) {
            if (typeof node.icon === 'string') {
                return node.icon;
            }
            if (this.treeControl.isExpanded(node) && node.children.length !== 0) {
                return node.icon.open;
            } else {
                return node.icon.closed;
            }
        } else {
            return;
        }
    }

    /**
     * Получить иконку по умолчанию
     * @param node
     */
    public getDefaultNodeIcon(node: INode) {
        let icon;
        if (node.is_folder == '1') {
            icon = {
                open: 'folder-open-outline',
                closed: 'folder-outline',
            };
        } else {
            icon = 'file-document-outline';
        }
        return icon;
    }
}
